import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { X, CircleCheck, AlertTriangle, CircleAlert } from "lucide-react";

const AlertNotification = ({ type, recordName, action, onClose, message, details }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false);
      if (onClose) onClose();
    }, 5000); // show for 5 seconds
    return () => clearTimeout(timer);
  }, [onClose]);

  if (!visible) return null;

  const getMessage = () => {
  if (message) return message;
  if (type === "success" && action === "create")
    return `${recordName} Saved Successfully`;
  if (type === "success" && action === "delete")
    return `${recordName} Deleted Successfully`;
  if (type === "error" && action === "delete")
    return `Error in ${recordName} Delete`;
  if (type === "success" && action === "update")
    return `${recordName} Saved Successfully`;
  if (type === "success" && action === "stockOut")
    return "Item Stocked Out Successfully";
  if (type === "success" && action === "discard")
    return `${recordName} Discarded Successfully`;
  if (type === "warning" && action === "discard")
    return `${recordName} Discarded`;
  if (type === "error" && action === "create")
    return `Error Saving`;
  if (type === "error" && action === "update")
    return `Error Saving`;
  if (type === "error" && action === "fetch")
    return `Error Loading ${recordName}`;
  if (type === "success" && action === "activate")
    if (recordName === "Category" || recordName === "Branch" || recordName === "Stock" || recordName === "Listing") {
      return `${recordName} Activated Successfully`;
    } else if (recordName === "SPU") {
      return `${recordName} Activated Successfully`; // Added for SPU
    } else {
      return `${recordName} Activated`;
    }
  if (type === "success" && action === "deactivate")
    if (recordName === "Category" || recordName === "Branch" || recordName === "Stock" || recordName === "Listing") {
      return `${recordName} Deactivated Successfully`;
    } else if (recordName === "SPU") {
      return `${recordName} Deactivated Successfully`; // Added for SPU
    } else {
      return `${recordName} Deactivated`;
    }
  if (type === "success" && action === "archive")
    if (recordName === "Category" || recordName === "Stock") {
      return `${recordName} Archived Successfully`;
    } else if (recordName === "Listing") {
      return `Archived ${recordName} Successfully`;
    }
    else if (recordName === "SPU") {
      return `${recordName} Archived Successfully`; // Added for SPU
    } else {
      return `${recordName} Archived`;
    }
if (type === "success" && action === "enable")
  return `${recordName} Enabled Successfully`;
if (type === "success" && action === "disable")
  return `${recordName} Disabled Successfully`;
  return "";
};

  // Determine maxWidth based on recordName
  const getWidth = () => {
    if (action === "activate" || action === "deactivate") {
      return "260px"; // narrower for short messages
    } else if (action === "discard") {
      return "300px";
    }
    return "360px"; // default width for others
  };


  return (
    <div
      role="alert"
      style={{
        // position: "absolute",
        position: "fixed",       // use fixed so it stays visible on scroll
        top: "86px",
        // left: "50%",             // center horizontally
        left: "50%",                  // center horizontally
        transform: "translateX(-50%)", // ensure perfect centering
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        alignSelf: "center",
        padding: `${type === "success" ? "10px 16px" : "10px"}`,
        borderRadius: "12px",
        boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.12)",
        fontSize: "12px",
        fontWeight: 700,
        fontFamily: "Montserrat",
        lineHeight: 'normal',
        // `details` (ERP-5170) turns the toast into a small block: the title on
        // top, a breakdown under it. Without it nothing changes — same single
        // row, same fixed 50px height every other screen already relies on.
        flexDirection: details ? "column" : "row",
        alignItems: details ? "flex-start" : "center",
        gap: details ? "6px" : "40px",
        height: details ? "auto" : "50px",
        maxWidth: details ? "min(92vw, 460px)" : undefined,
        border: `1px solid ${type === "success"
          ? "var(--Green-500, #198754)"
          : type === "warning" ? '#CC9A06' : "var(--Red-500, #DC3545)"
          }`,
        background: `${type === "success"
          ? "var(--Green-100, #D1E7DD)"
          : type === "warning" ? "#FFF3CD" : "var(--Red-100, #F8D7DA)"
          }`,
        color: `${type === "success"
          ? "var(--Green-800, #0A3622)"
          : type === "warning" ? '#664D03' : "var(--Red-800, #58151C)"
          }`,
        zIndex: 9999,            // ensures it appears above everything
      }}
    >
      <div className="d-flex align-items-center" style={{ gap: "8px" }}>
        {type === "success" ? (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <g clip-path="url(#clip0_4070_28573)">
              <path d="M7.49935 9.99984L9.16602 11.6665L12.4993 8.33317M18.3327 9.99984C18.3327 14.6022 14.6017 18.3332 9.99935 18.3332C5.39698 18.3332 1.66602 14.6022 1.66602 9.99984C1.66602 5.39746 5.39698 1.6665 9.99935 1.6665C14.6017 1.6665 18.3327 5.39746 18.3327 9.99984Z" stroke="#0A3622" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </g>
            <defs>
              <clipPath id="clip0_4070_28573">
                <rect width="20" height="20" fill="white" />
              </clipPath>
            </defs>
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M9.99935 6.6665V9.99984M9.99935 13.3332H10.0077M18.3327 9.99984C18.3327 14.6022 14.6017 18.3332 9.99935 18.3332C5.39698 18.3332 1.66602 14.6022 1.66602 9.99984C1.66602 5.39746 5.39698 1.6665 9.99935 1.6665C14.6017 1.6665 18.3327 5.39746 18.3327 9.99984Z" stroke={type === "warning" ? "#664D03" : "#58151C"} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        )}
        <strong>{getMessage()}</strong>
      </div>
      {details && (
        <div style={{ paddingLeft: 28, fontWeight: 400, width: "100%" }}>
          {details}
        </div>
      )}
    </div>
  );
};

AlertNotification.propTypes = {
  type: PropTypes.oneOf(["success", "error", "warning"]).isRequired,
  recordName: PropTypes.string.isRequired,
  action: PropTypes.oneOf([
    "create",
    "update",
    "discard",
    "activate",
    "deactivate",
    "delete",
    "stockOut",
    "fetch",
    "archive",
    "enable",
    "disable",
  ]).isRequired,
  message: PropTypes.string,
  // Optional block rendered under the title — see ERP-5170's save toast.
  details: PropTypes.node,
  onClose: PropTypes.func,
};


export default AlertNotification;